import { Select } from "antd";
import Fuse from "fuse.js";

import { IGroupByListColumn } from "models";

import { FORECAST_KEY, SYNC_KEY } from "../../../../constants/settings.constants";
import {
  ForecastArrayProduct,
  forecastArrayProductOptions
} from "../../../arps/widget/TypeWellEditor/tabs/ForecastArrayProductOptions";
import { TypeWellProductItem } from "../../../arps/widget/TypeWellEditor/tabs/TypeWellProductOptions";
import { UPLOAD_FILE_TYPES, UploadFileType } from "./constants";

export const findBestProductMatch = (
  detectedProduct: string,
  availableOptions: Array<TypeWellProductItem | ForecastArrayProduct>
): { key?: string; isExactMatch: boolean } => {
  const normalizedProduct = detectedProduct.toLowerCase().trim();

  // Step 1: Direct matches (exact match)
  const exactMatch = availableOptions.find(
    (option) =>
      option.label.toLowerCase() === normalizedProduct ||
      option.key.toLowerCase() === normalizedProduct
  );
  if (exactMatch) return { key: exactMatch.key, isExactMatch: true };

  // Step 2: Known aliases mapping
  const commonMappings: Record<string, string[]> = {
    CONDENSATE: ["field condensate", "plant condensate", "cond", "cond.", "condy"],
    GAS: ["gas production", "produced gas", "raw gas"],
    "SALES GAS": ["sales gas", "sales gas production"],
    OIL: ["crude oil", "crude", "oil production"],
    WATER: ["produced water", "water production"],
    CGR: ["condensate gas ratio", "cond gas ratio"],
    GOR: ["gas oil ratio", "gas-oil ratio"],
    WOR: ["water oil ratio", "water-oil ratio"],
    WGR: ["water gas ratio", "water-gas ratio"]
  };

  // Check if the detected product matches any known aliases
  for (const [productKey, aliases] of Object.entries(commonMappings)) {
    if (aliases.some((alias) => normalizedProduct.includes(alias))) {
      const matchedProduct = availableOptions.find(
        (option) =>
          option.key.toLowerCase() === productKey.toLowerCase() ||
          option.label.toLowerCase() === productKey.toLowerCase()
      );
      if (matchedProduct) return { key: matchedProduct.key, isExactMatch: false };
    }
  }

  // Step 3: Fuzzy matching via Fuse.js as fallback
  const options = {
    keys: ["label", "key"],
    threshold: 0.2, // Lower threshold means more strict matching
    includeScore: true,
    ignoreLocation: true
  };

  const fuse = new Fuse(availableOptions, options);
  const result = fuse.search(detectedProduct);

  // Return the best match if one is found with a good score
  if (result.length > 0 && result[0].score < 0.5) {
    return { key: result[0].item.key, isExactMatch: false };
  }

  return { key: undefined, isExactMatch: false };
};

export const getDuplicateMappings = (productMappings: Record<string, string>) => {
  const mappingCounts: Record<string, number> = {};
  Object.values(productMappings).forEach((mappedTo) => {
    if (mappedTo) {
      mappingCounts[mappedTo] = (mappingCounts[mappedTo] || 0) + 1;
    }
  });
  return Object.keys(mappingCounts).filter((key) => mappingCounts[key] > 1);
};

export const getProductOptions = (
  uploadFileType: UploadFileType,
  supportedProducts: TypeWellProductItem[]
) => {
  if (uploadFileType === UPLOAD_FILE_TYPES.FORECAST_ARRAY) {
    return forecastArrayProductOptions.map((option: ForecastArrayProduct) => (
      <Select.Option key={option.key} value={option.key}>
        {option.label}
      </Select.Option>
    ));
  } else {
    return supportedProducts.map((supportedProduct) => (
      <Select.Option key={supportedProduct.key} value={supportedProduct.key}>
        {supportedProduct.label}
      </Select.Option>
    ));
  }
};

/**
 * GroupBy appends forecast/sync suffixes to Performance field properties; strip them
 * so well data mappings resolve to base column properties from useColumns().
 */
export const normalizeGroupByProperty = (property?: string | null) => {
  if (!property) return property;
  const forecastToken = `.${FORECAST_KEY}`;
  const forecastIndex = property.indexOf(forecastToken);
  if (forecastIndex > -1) {
    return property.slice(0, forecastIndex);
  }
  const syncToken = `.${SYNC_KEY}.`;
  const syncIndex = property.indexOf(syncToken);
  if (syncIndex > -1) {
    return property.slice(0, syncIndex);
  }
  return property;
};

/**
 * Known mappings for common well data column names to target field properties.
 * Keys are lowercase for case-insensitive matching.
 */
const WELL_DATA_COLUMN_MAPPINGS: Record<string, string> = {
  // HzLength variations
  "hz length": "Well_Design.HzLength_m",
  hzlength: "Well_Design.HzLength_m",
  "horizontal length": "Well_Design.HzLength_m",
  "lateral length": "Well_Design.HzLength_m",
  "hz len": "Well_Design.HzLength_m",
  length: "Well_Design.HzLength_m",

  // StageSpacing variations
  "stage spacing": "Frac_Data.StageSpacing_m",
  stagespacing: "Frac_Data.StageSpacing_m",
  "frac spacing": "Frac_Data.StageSpacing_m",
  spacing: "Frac_Data.StageSpacing_m",

  // ProppantIntensity variations
  "proppant intensity": "Frac_Data.ProppantIntensity_tm",
  proppantintensity: "Frac_Data.ProppantIntensity_tm",
  "proppant per foot": "Frac_Data.ProppantIntensity_tm",
  "proppant/m": "Frac_Data.ProppantIntensity_tm",
  "proppant per meter": "Frac_Data.ProppantIntensity_tm",
  intensity: "Frac_Data.ProppantIntensity_tm",

  // Stage count variations
  stages: "Frac_Data.Stages",
  "number of stages": "Frac_Data.Stages",
  "num stages": "Frac_Data.Stages",
  "stage count": "Frac_Data.Stages",

  // Fluid variations
  "fluid total": "Frac_Data.FluidTotal_m3",
  fluidtotal: "Frac_Data.FluidTotal_m3",
  "total fluid": "Frac_Data.FluidTotal_m3",
  fluid: "Frac_Data.FluidTotal_m3",

  // Proppant total variations
  proppant: "Frac_Data.ProppantTotal_t",
  "proppant total": "Frac_Data.ProppantTotal_t",
  "total proppant": "Frac_Data.ProppantTotal_t"
};

/**
 * Finds the best matching column for a detected well data header.
 * Uses known aliases first, then fuzzy matching as fallback.
 *
 * @param detectedHeader - The column header from the uploaded Excel file
 * @param availableColumns - All available numeric columns from useColumns()
 * @returns The matched column and whether it was an exact/known match
 */
export const findBestWellDataColumnMatch = (
  detectedHeader: string,
  availableColumns: IGroupByListColumn[]
): { column?: IGroupByListColumn; isExactMatch: boolean } => {
  const normalizedHeader = detectedHeader.toLowerCase().trim();

  // Step 1: Check known aliases mapping
  const knownMapping = WELL_DATA_COLUMN_MAPPINGS[normalizedHeader];
  if (knownMapping) {
    const matchedColumn = availableColumns.find(
      (col) => col.property.toLowerCase() === knownMapping.toLowerCase()
    );
    if (matchedColumn) {
      return { column: matchedColumn, isExactMatch: true };
    }
  }

  // Step 2: Direct match on property or title
  const directMatch = availableColumns.find(
    (col) =>
      col.property.toLowerCase() === normalizedHeader ||
      col.title.toLowerCase() === normalizedHeader
  );
  if (directMatch) {
    return { column: directMatch, isExactMatch: true };
  }

  // Step 3: Partial match in known mappings (check if header contains a known key)
  for (const [aliasKey, targetProperty] of Object.entries(WELL_DATA_COLUMN_MAPPINGS)) {
    if (normalizedHeader.includes(aliasKey) || aliasKey.includes(normalizedHeader)) {
      const matchedColumn = availableColumns.find(
        (col) => col.property.toLowerCase() === targetProperty.toLowerCase()
      );
      if (matchedColumn) {
        return { column: matchedColumn, isExactMatch: false };
      }
    }
  }

  // Step 4: Fuzzy matching via Fuse.js as fallback
  const fuseOptions = {
    keys: ["title", "property"],
    threshold: 0.3,
    includeScore: true,
    ignoreLocation: true
  };

  const fuse = new Fuse(availableColumns, fuseOptions);
  const result = fuse.search(detectedHeader);

  // Return the best match if one is found with a good score
  if (result.length > 0 && result[0].score < 0.4) {
    return { column: result[0].item, isExactMatch: false };
  }

  return { column: undefined, isExactMatch: false };
};
